import { useMemo, useState } from 'react';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
    useStaffPerformanceReport,
    useExportStaffPerformance,
    type StaffPerformanceFilter,
} from '@/api/reports';
import { useUserDirectory } from '@/api/users';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/FormField';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { toIsoDate } from '@/lib/calendar';
/**
 * Staff Performance — per-staff rollup of completion %, cancellation rate,
 * note completion rate, and hours. Completion denominator excludes Scheduled
 * (a future session isn't a "miss"). Note-rate denominator is completed
 * sessions only.
 */

export function StaffPerformancePage() {
    const canExport = useAuthStore((s) => s.hasPermission('export_reports'));

    const [filter, setFilter] = useState<StaffPerformanceFilter>({});
    const [draft, setDraft]   = useState<StaffPerformanceFilter>({});

    const { data, isLoading, isError, error, isFetching } = useStaffPerformanceReport(filter);
    const exportM = useExportStaffPerformance();
    const staffQuery = useUserDirectory();

    const staffOptions = useMemo(() => {
        if (!staffQuery.data) return [];
        return staffQuery.data
            .map((s) => ({ id: s.id, label: `${s.last}, ${s.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [staffQuery.data]);

    function apply() { setFilter({ ...draft }); }
    function clear() { setDraft({}); setFilter({}); }

    async function runExport() {
        try { await exportM.mutateAsync(filter); } catch { /* isError renders below */ }
    }

    return (
        <div className="p-6 max-w-[1400px] mx-auto space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/reporting" className="text-xs text-text-subtle hover:text-text inline-flex items-center gap-1 mb-1">
                        <ArrowLeft className="w-3 h-3" /> All reports
                    </Link>
                    <h1 className="font-display text-2xl font-bold text-text">Staff Performance</h1>
                    <p className="text-xs text-text-subtle">
                        Completion and note-completion rates per staff member. Completion % excludes Scheduled sessions; note % is over completed sessions only.
                    </p>
                </div>
                {canExport && (
                    <Button variant="primary" size="sm" onClick={runExport} disabled={exportM.isPending || isLoading}>
                        {exportM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        Export CSV
                    </Button>
                )}
            </div>

            <Card>
                <CardBody>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                        <label className="text-xs block">
                            <span className="text-text-muted">Staff</span>
                            <select
                                value={draft.staffId ?? ''}
                                onChange={(e) => setDraft({ ...draft, staffId: e.target.value || undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All staff</option>
                                {staffOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Role</span>
                            <select
                                value={draft.roleType ?? ''}
                                onChange={(e) => setDraft({ ...draft, roleType: (e.target.value || undefined) as 'BCBA' | 'RBT' | undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All roles</option>
                                <option value="BCBA">BCBA</option>
                                <option value="RBT">RBT</option>
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Date from</span>
                            <Input
                                type="date"
                                min="1900-01-01"
                                max={toIsoDate(new Date())}
                                value={draft.dateFrom ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Date to</span>
                            <Input
                                type="date"
                                min={draft.dateFrom || '1900-01-01'}
                                max={toIsoDate(new Date())}
                                value={draft.dateTo ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateTo: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        <Button variant="primary" size="sm" onClick={apply}>Apply</Button>
                        <Button variant="ghost" size="sm" onClick={clear}>Clear</Button>
                        {isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin text-text-subtle" />}
                    </div>
                </CardBody>
            </Card>

            {isError ? (
                <Card>
                    <CardBody>
                        <p className="text-sm text-error font-medium">Couldn't load report</p>
                        <p className="text-xs text-text-subtle mt-1">{extractErrorMessage(error, 'Please try again.')}</p>
                    </CardBody>
                </Card>
            ) : isLoading || !data ? (
                <Card><CardBody><div className="flex items-center gap-2 text-text-subtle text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></CardBody></Card>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                        <Kpi label="Staff" value={data.kpis.totalStaff.toLocaleString()} />
                        <Kpi label="Sessions" value={data.kpis.totalSessions.toLocaleString()} />
                        <Kpi label="Completed" value={data.kpis.completed.toLocaleString()} />
                        <Kpi label="Canceled" value={data.kpis.canceled.toLocaleString()} />
                        <Kpi label="Notes missing" value={data.kpis.notesMissing.toLocaleString()} emphasis={data.kpis.notesMissing > 0} />
                        <Kpi label="Hours" value={data.kpis.hours.toLocaleString()} />
                    </div>

                    <Card>
                        <CardHeader><CardTitle>Per staff</CardTitle></CardHeader>
                        <CardBody className="pt-0 overflow-x-auto">
                            {data.staff.length === 0 ? (
                                <p className="text-xs text-text-subtle px-3 py-4">No sessions match the current filter.</p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">Staff</th>
                                            <th className="px-3 py-2 font-bold">Role</th>
                                            <th className="px-3 py-2 font-bold text-right">Total</th>
                                            <th className="px-3 py-2 font-bold text-right">Completed</th>
                                            <th className="px-3 py-2 font-bold text-right">Canceled</th>
                                            <th className="px-3 py-2 font-bold text-right">% Completed</th>
                                            <th className="px-3 py-2 font-bold text-right">Notes submitted</th>
                                            <th className="px-3 py-2 font-bold text-right">Notes missing</th>
                                            <th className="px-3 py-2 font-bold text-right">% Notes done</th>
                                            <th className="px-3 py-2 font-bold text-right">Hours</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.staff.map((r) => (
                                            <tr key={r.staff_id ?? r.staff_name} className="border-b border-border last:border-b-0">
                                                <td className="px-3 py-2">{r.staff_name}</td>
                                                <td className="px-3 py-2 text-xs">{r.role_type || '—'}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.total}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.completed}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.canceled}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.completion_rate.toFixed(0)}%</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.notes_submitted}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">
                                                    {r.notes_missing > 0
                                                        ? <span className="text-warning-text font-medium">{r.notes_missing}</span>
                                                        : '—'}
                                                </td>
                                                <td className="px-3 py-2 text-right tabular-nums">
                                                    <span className={r.note_rate < 80 && r.completed > 0 ? 'text-warning-text' : ''}>
                                                        {r.note_rate.toFixed(0)}%
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.hours}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </CardBody>
                    </Card>
                </>
            )}
        </div>
    );
}

function Kpi({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
    return (
        <Card>
            <CardBody>
                <p className="text-2xs uppercase tracking-wider text-text-subtle">{label}</p>
                <p className={`font-display text-xl font-bold mt-1 tabular-nums ${emphasis ? 'text-warning-text' : 'text-text'}`}>{value}</p>
            </CardBody>
        </Card>
    );
}
